import type {
  ActivityEventType,
  AlertTier,
  HealthStatus,
  MilestoneStatus,
  OverallHealthStatus,
} from '../types';

/**
 * Health thresholds for determining status colors
 * Values are percentages (0-100) or rating (0-5)
 */
export const HEALTH_THRESHOLDS = {
  attendance: {
    healthy: 80, // >= 80% is healthy
    warning: 60, // >= 60% is warning, < 60% is critical
  },
  taskCompletion: {
    healthy: 70, // >= 70% is healthy
    warning: 50, // >= 50% is warning, < 50% is critical
  },
  satisfaction: {
    healthy: 4.0, // >= 4.0 is healthy
    warning: 3.0, // >= 3.0 is warning, < 3.0 is critical
  },
  /**
   * MED-1205 (C2b-ii): facilitator airtime (leader/coach share of talk time).
   * INVERTED — a HIGHER leader share is worse — and role-aware by the cohort's
   * leadership modality (Brook-confirmed 7/13). The `red` cut mirrors B7's
   * facilitator_heavy_threshold_pct (MED-894: coach-led 40, peer-led 50) so a
   * red metric and a facilitator-heavy feedback message never disagree (AC3).
   * The `orange` cut is the C2b-ii warning band (below facilitator-heavy).
   */
  airtime: {
    coach_led: { red: 40, orange: 30 }, // >= 40 red, >= 30 orange, < 30 green
    peer_led: { red: 50, orange: 40 }, // >= 50 red, >= 40 orange, < 40 green
  },
} as const;

/** MED-1205: airtime metric color tier (inverted, role-aware). */
export type AirtimeColorTier = 'red' | 'orange' | 'green';

/**
 * MED-1205 (C2b-ii / AC2): map a session's leader/coach airtime share to a
 * color tier. Inverted (higher share = worse) and role-aware — peer-led
 * cohorts tolerate a higher leader share before flagging. Reads the cutoffs
 * from HEALTH_THRESHOLDS.airtime so the red band stays pinned to B7's
 * facilitator-heavy threshold (AC3).
 */
export function getAirtimeColorTier(
  leaderSharePct: number,
  isPeerLed: boolean,
): AirtimeColorTier {
  const cutoffs = isPeerLed
    ? HEALTH_THRESHOLDS.airtime.peer_led
    : HEALTH_THRESHOLDS.airtime.coach_led;
  if (leaderSharePct >= cutoffs.red) return 'red';
  if (leaderSharePct >= cutoffs.orange) return 'orange';
  return 'green';
}

/**
 * Health status color mapping for Mantine
 */
export const HEALTH_STATUS_COLORS: Record<HealthStatus, string> = {
  healthy: 'medleyGreen',
  warning: 'warningAmber',
  critical: 'dangerRed',
  no_data: 'gray',
};

/**
 * Health status labels
 */
export const HEALTH_STATUS_LABELS: Record<HealthStatus, string> = {
  healthy: 'Healthy',
  warning: 'Needs Attention',
  critical: 'Critical',
  no_data: 'No Data',
};

/**
 * Activity type configuration
 */
export const ACTIVITY_TYPE_CONFIG: Record<
  ActivityEventType,
  {
    label: string;
    color: string;
    icon:
      | 'user-plus'
      | 'check-circle'
      | 'users'
      | 'briefcase'
      | 'file-text'
      | 'calendar'
      | 'refresh-cw'
      | 'settings'
      | 'mail';
  }
> = {
  participant_added: {
    label: 'Participant Added',
    color: 'medleyGreen',
    icon: 'user-plus',
  },
  task_completed: {
    label: 'Task Completed',
    color: 'medleyGreen',
    icon: 'check-circle',
  },
  group_created: {
    label: 'Group Created',
    color: 'blue',
    icon: 'users',
  },
  coach_assigned: {
    label: 'Coach Assigned',
    color: 'violet',
    icon: 'briefcase',
  },
  intake_completed: {
    label: 'Intake Completed',
    color: 'teal',
    icon: 'file-text',
  },
  dates_updated: {
    label: 'Dates Updated',
    color: 'orange',
    icon: 'calendar',
  },
  survey_scheduled: {
    label: 'Survey Scheduled',
    color: 'surveyPurple',
    icon: 'file-text',
  },
  survey_cancelled: {
    label: 'Survey Cancelled',
    color: 'gray',
    icon: 'file-text',
  },
  survey_sent: {
    label: 'Survey Sent',
    color: 'surveyPurple',
    icon: 'file-text',
  },
  status_changed: {
    label: 'Status Changed',
    color: 'warningAmber',
    icon: 'refresh-cw',
  },
  configuration_updated: {
    label: 'Configuration Updated',
    color: 'gray',
    icon: 'settings',
  },
  absence_follow_up_sent: {
    label: 'Follow-Up Sent',
    color: 'warningAmber',
    icon: 'mail',
  },
  session_invites_sent: {
    label: 'Invites Sent',
    color: 'medleyGreen',
    icon: 'mail',
  },
  calendar_sync_failed: {
    label: 'Calendar Sync Failed',
    color: 'dangerRed',
    icon: 'calendar',
  },
  pre_session_reflection_prompt_sent: {
    label: 'Reflection Prompt Sent',
    color: 'teal',
    icon: 'mail',
  },
  pre_session_reflection_reminder_sent: {
    label: 'Reflection Reminder Sent',
    color: 'warningAmber',
    icon: 'mail',
  },
};

/**
 * Milestone status configuration
 */
export const MILESTONE_STATUS_CONFIG: Record<
  MilestoneStatus,
  {
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  not_started: {
    label: 'Not Started',
    color: 'gray.6',
    bgColor: 'gray.1',
    borderColor: 'transparent',
  },
  in_progress: {
    label: 'In Progress',
    color: 'warningAmber.7',
    bgColor: 'warningAmber.0',
    borderColor: 'warningAmber.5',
  },
  completed: {
    label: 'Completed',
    color: 'medleyGreen.7',
    bgColor: 'medleyGreen.0',
    borderColor: 'transparent',
  },
};

/**
 * Stat card configuration for the overview grid
 */
export const STAT_CARD_CONFIG = {
  sessions: {
    label: 'Sessions',
    color: 'blue',
  },
  participants: {
    label: 'Participants',
    color: 'medleyGreen',
  },
  groups: {
    label: 'Groups',
    color: 'violet',
  },
  tasks: {
    label: 'Tasks',
    color: 'orange',
  },
  attendance: {
    label: 'Avg Attendance',
    color: 'teal',
  },
  rating: {
    label: 'Avg Rating',
    color: 'pink',
  },
} as const;

/**
 * MED-114: Health tier display configuration
 */
export const HEALTH_TIER_CONFIG: Record<AlertTier, {
  label: string;
  dotColor: string;
  buttonVariant: 'filled' | 'outline' | 'subtle';
}> = {
  risk: {
    label: 'Risks',
    dotColor: '#D64545',
    buttonVariant: 'filled',
  },
  monitor: {
    label: 'Monitor',
    dotColor: '#E9A23B',
    buttonVariant: 'outline',
  },
  on_track: {
    label: 'On Track',
    dotColor: '#4A7C59',
    buttonVariant: 'subtle',
  },
  notification: {
    label: 'Notifications',
    dotColor: '#3B82F6',
    buttonVariant: 'outline',
  },
};

/**
 * MED-114: Overall health status display configuration
 */
export const OVERALL_HEALTH_CONFIG: Record<OverallHealthStatus, {
  label: string;
  color: string;
}> = {
  at_risk: {
    label: 'At Risk',
    color: 'dangerRed',
  },
  monitor: {
    label: 'Monitor',
    color: 'warningAmber',
  },
  on_track: {
    label: 'Healthy',
    color: 'medleyGreen',
  },
  no_data: {
    label: 'No Data',
    color: 'gray',
  },
};
